/**
 * cart-tiers.js
 * Powers the cart tier progress bar and free-product auto-add/remove.
 * Config is injected by cart-tiers.liquid via theme.tieredProducts / theme.cartTiers.
 */
(function () {
  'use strict';

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!window.theme?.useTieredProducts) return;

  const tiers  = window.theme.tieredProducts  || {};
  const api    = window.theme.cartTiers?.api;
  const evts   = window.theme.cartTiers?.events || {};

  if (!api || !Object.keys(tiers).length) return;

  const tierKeys = Object.keys(tiers)
    .map(Number)
    .sort((a, b) => tiers[a].amount - tiers[b].amount);

  const maxAmount = tiers[tierKeys[tierKeys.length - 1]].amount;

  // ── Populate legend template into every [data-cart-goal] ─────────────────
  const tmpl       = document.getElementById('cart-goal-template');
  const containers = [...document.querySelectorAll('[data-cart-goal]')];

  containers.forEach(c => {
    if (tmpl) {
      const legend = c.querySelector('.cart-goal-legend');
      if (legend && !legend.children.length) {
        legend.appendChild(tmpl.content.cloneNode(true));
      }
    }
    // Banner container has its own conditional — only show if setting is on
    if (c.classList.contains('cart-goal-banner-container')) {
      if (window.theme?.showTiersInBanner) c.style.display = '';
    } else {
      c.style.display = '';
    }
  });

  // Populate cart banner slot
  const bannerTmpl = document.getElementById('cart-banner-template');
  const bannerSlot = document.getElementById('cart-banner-slot');
  if (bannerTmpl && bannerSlot && !bannerSlot.children.length) {
    bannerSlot.appendChild(bannerTmpl.content.cloneNode(true));
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function fmt(cents) {
    return api.formatMoney(cents);
  }

  function stripTags(html) {
    return (html || '').replace(/<[^>]*>/g, '');
  }

  function interpolate(richText, cents) {
    const amount = fmt(cents);
    return (richText || '').replace(/\[amount\]/gi, amount);
  }

  function setProgress(fraction) {
    const clamped = Math.min(Math.max(fraction, 0), 1);
    containers.forEach(c => {
      const bar = c.querySelector('.progress-bar');
      if (bar) bar.style.transform = `scaleX(${clamped})`;
    });
  }

  function setMessage(html) {
    containers.forEach(c => {
      const el = c.querySelector('.next-reach, [class*="rte"]');
      if (el) el.innerHTML = html || '';
    });
  }

  function setActivation(tierKey, on) {
    const idx = tierKeys.indexOf(Number(tierKey));
    if (idx === -1) return;
    containers.forEach(c => {
      const items = c.querySelectorAll('.cart-goal-legend li');
      if (items[idx]) items[idx].classList.toggle('activated', on);
    });
  }

  // ── Core update ───────────────────────────────────────────────────────────
  let _busy = false;

  async function applyCart(cart) {
    const total = cart.total_price || 0;

    // Progress bar
    setProgress(total / maxAmount);

    // Tier activation + message
    tierKeys.forEach(k => setActivation(k, total >= tiers[k].amount));

    const nextKey = tierKeys.find(k => total < tiers[k].amount);
    if (nextKey !== undefined) {
      const needed = tiers[nextKey].amount - total;
      const rawDesc = tiers[nextKey].goal_description;
      const tierLabel = tiers[nextKey].label || 'your next reward';
      const desc = (rawDesc && rawDesc.trim())
        ? rawDesc
        : `<p>[amount] away from ${tierLabel}!</p>`;
      setMessage(interpolate(desc, needed));
    } else {
      setMessage(window.theme.tierSuccess || '<p>You\'ve unlocked all rewards!</p>');
    }

    // ── Free product auto-add / remove ────────────────────────────────────
    if (_busy) return;
    _busy = true;

    try {
      for (const k of tierKeys) {
        const tier = tiers[k];
        if (!['free_product', 'choose_variant'].includes(tier.type)) continue;
        if (!tier.variant_id) continue;

        const reached = total >= tier.amount;
        const freeItem = (cart.items || []).find(
          i => i.variant_id === tier.variant_id && i.properties?._free_tier
        );

        if (reached && !freeItem) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: tier.variant_id, quantity: 1, properties: { _free_tier: '1' } }]
            })
          });
          refreshCart();
        } else if (!reached && freeItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: freeItem.key, quantity: 0 })
          });
          refreshCart();
        }
      }
    } finally {
      _busy = false;
    }
  }

  function refreshCart() {
    api.getCart().then(cart => {
      document.dispatchEvent(new CustomEvent(evts.cartBuild || 'cart:build', { detail: { cart } }));
    }).catch(() => {});
  }

  function fetchAndApply() {
    api.getCart().then(applyCart).catch(() => {});
  }

  // ── Event wiring ──────────────────────────────────────────────────────────
  [evts.cartUpdated || 'cart:updated',
   evts.cartBuild   || 'cart:build',
   evts.productAdded || 'ajaxProduct:added'
  ].forEach(name => {
    document.addEventListener(name, fetchAndApply);
  });

  // Initial render
  fetchAndApply();

})();
